import { useCallback } from 'react';
import { get, set, isNil } from 'lodash';
import { useChatPromptVersion } from '../../../../components/ChatAgent/hooks/useChatPromptVersion';
import { tabId, APP_EVENT_TYPE, useAppEventContext } from '../../app-events-aggregator';
import { useWebSocket } from '../../web-socket-provider/hooks/useWebSocket';
import { CHAT_AGENT_COMPONENT_TYPE } from '../config/chatAgentComponentType';
import { CHAT_DIALOG_MESSAGE_PROP_PATH } from '../config/chatDialogMessagePropPath';
import { useChatAgentContextService } from './useChatAgentContextService';
import { useChatAgentSessionId } from './useChatAgentSessionId';

const COMPONENT_TO_IGNORE = {
    [CHAT_AGENT_COMPONENT_TYPE.PERFORM_NOW]: true,
    [CHAT_AGENT_COMPONENT_TYPE.TEXT]: true,
    [CHAT_AGENT_COMPONENT_TYPE.QUICK_ANSWERS]: true,
    [CHAT_AGENT_COMPONENT_TYPE.QUICK_ANSWERS_LIST]: true,
    [CHAT_AGENT_COMPONENT_TYPE.ONBOARDING_START_CREATION_FLOW]: true,
    [CHAT_AGENT_COMPONENT_TYPE.DETAILED_ACTION_MODAL]: true,
    [CHAT_AGENT_COMPONENT_TYPE.LOADER]: true,
};

export const useChatDialogMessagesConnection = ({ getTool, sendResponseToChatAgent, setSessionId }) => {
    const [promptVersion] = useChatPromptVersion();
    const { push: pushAppEvent } = useAppEventContext();
    const { getContext } = useChatAgentContextService();
    const [sessionId] = useChatAgentSessionId();

    const onWebSocketMessage = useCallback(
        message => {
            const { componentName: methodName } = message?.componentData || {};
            const sessionId = message?.sessionId;

            if (!isNil(sessionId)) {
                setSessionId(sessionId);
            }

            if (!methodName || COMPONENT_TO_IGNORE[methodName?.toUpperCase()] || COMPONENT_TO_IGNORE[methodName]) {
                sendResponseToChatAgent({ [CHAT_DIALOG_MESSAGE_PROP_PATH]: message, actions: [] });
                return;
            }

            const tool = getTool(methodName, message);
            if (!tool) {
                return;
            }
            tool();
        },
        [getTool, setSessionId, sendResponseToChatAgent]
    );

    useWebSocket(onWebSocketMessage);

    const isChatDialogMessage = useCallback(event => {
        return get(event?.data, CHAT_DIALOG_MESSAGE_PROP_PATH);
    }, []);

    const sendUserChatTextMessageToGW = useCallback(
        async windowMessage => {
            const message = windowMessage.data;
            if (!isChatDialogMessage(windowMessage)) {
                return;
            }

            const { appContext, chatContext, formContext } = await getContext();
            const { [CHAT_DIALOG_MESSAGE_PROP_PATH]: chatDialogMessage } = message;
            set(chatDialogMessage, 'flows[0].appContext', appContext);
            set(chatDialogMessage, 'flows[0].chatContext', chatContext);
            set(chatDialogMessage, 'flows[0].formContext', formContext || {});

            const extendedMessage = {
                ...chatDialogMessage,
                tabId,
                sessionId,
                promptVersion,
            };

            if (!extendedMessage.componentData) {
                return;
            }

            pushAppEvent({ type: APP_EVENT_TYPE.CHAT_DIALOG_MESSAGE, data: extendedMessage });
        },
        [getContext, isChatDialogMessage, promptVersion, pushAppEvent, sessionId]
    );

    return { sendUserChatTextMessageToGW, isChatDialogMessage };
};
